import React from 'react';
import { Wrench, MapPin, Clock, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { trackEvent } from '../seo/analytics';

export const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-gray-100 -z-10" />
      <div
        className="absolute inset-0 opacity-[0.03] -z-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-red-50 rounded-full mb-6">
              <MapPin className="w-4 h-4 text-red-600 mr-2" />
              <span className="text-sm font-semibold text-red-600">Mobile Service - We Come To You!</span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
              <span className="block text-red-600">Vargas</span>
              Professional Mobile
              <span className="block text-red-600">Tire & Auto Repair</span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Expert automotive services delivered right to your location. From tire sales and installation to complete
              vehicle diagnostics - we bring the shop to you.
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Expert Technicians</h3>
                  <p className="text-sm text-gray-600">Certified professionals</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">24/7 Roadside Assistance</h3>
                  <p className="text-sm text-gray-600">Anytime, Anywhere</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('booking')}
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
              >
                Book Service Now
              </Button>
              <a
                href="tel:3057724150"
                onClick={() =>
                  trackEvent('generate_lead', {
                    event_category: 'engagement',
                    event_label: 'hero_call_now',
                    phone_number: '305-772-4150',
                  })
                }
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-gray-300 hover:border-red-600 hover:text-red-600 text-lg px-8 py-6 transition-all"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call (305) 772-4150
                </Button>
              </a>
            </div>

            {/* Emergency Roadside Assistance Button */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <a
                href="/roadside"
                onClick={() =>
                  trackEvent('roadside_request_started', {
                    event_category: 'engagement',
                    event_label: 'hero_need_roadside_help',
                  })
                }
              >
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all animate-pulse"
                >
                  <span className="text-2xl mr-3">🚨</span>
                  Need Roadside Help Now?
                </Button>
              </a>
              <p className="text-sm text-gray-600 mt-2 text-center sm:text-left">
                Flat tire? Dead battery? Get emergency assistance within minutes
              </p>
            </div>
          </div>

          {/* Right Content - Feature Cards */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl font-bold text-red-600 mb-2">15+</div>
                <div className="text-gray-600 font-medium">Years Experience</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow mt-8">
                <div className="text-4xl font-bold text-red-600 mb-2">24/7</div>
                <div className="text-gray-600 font-medium">Emergency Service</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl font-bold text-red-600 mb-2">5000+</div>
                <div className="text-gray-600 font-medium">Happy Customers</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow mt-8">
                <div className="text-4xl font-bold text-red-600 mb-2">100%</div>
                <div className="text-gray-600 font-medium">Satisfaction</div>
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-red-100 rounded-full opacity-50 blur-2xl -z-10" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-slate-200 rounded-full opacity-50 blur-2xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};